import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  Button,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Grid,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PlayCircle as PlayIcon,
  School as SchoolIcon,
  Timer as TimerIcon,
  PlayArrow as StartIcon,
  Article as LessonsIcon,
} from '@mui/icons-material';
import { courseApi } from '../services/api';

interface Lesson {
  id: string;
  slug: string;
  title: string;
  contentType: string;
  estimatedDurationMinutes: number | null;
  pointsReward: number;
  sortOrder: number;
}

interface Module {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  sortOrder: number;
  lessons: Lesson[];
}

interface Course {
  id: string;
  slug: string;
  title: string;
  modules: Module[];
  enrollment: { status: string; progressPercent: number } | null;
}

function ModuleDetailPage() {
  const { courseId, moduleId } = useParams<{ courseId: string; moduleId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Fetch course details (includes modules and lessons) using ID
  const { data: course, isLoading, error } = useQuery({
    queryKey: ['course', courseId],
    queryFn: () => courseApi.getCourse(courseId!).then((r) => r.data as Course),
    enabled: !!courseId,
  });

  // Enroll mutation
  const enrollMutation = useMutation({
    mutationFn: () => courseApi.enrollCourse(courseId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course', courseId] });
    },
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !course) {
    return (
      <Box>
        <Alert severity="error">
          {t('errors.notFound')} - Kurs nicht gefunden
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/courses')} sx={{ mt: 2 }}>
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  // Helper to check if string is UUID format
  const isUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

  // Find the specific module by ID or slug
  const module = course.modules.find((m) => m.id === moduleId || m.slug === moduleId);

  // Redirect to UUID-based URL if using slugs
  React.useEffect(() => {
    if (course && module && courseId && moduleId) {
      const courseIsSlug = !isUUID(courseId);
      const moduleIsSlug = !isUUID(moduleId);

      if (courseIsSlug || moduleIsSlug) {
        navigate(`/courses/${course.id}/modules/${module.id}`, { replace: true });
      }
    }
  }, [course, module, courseId, moduleId, navigate]);

  if (!module) {
    return (
      <Box>
        <Alert severity="error">
          Modul nicht gefunden
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate(`/courses/${course.id}`)} sx={{ mt: 2 }}>
          Zurueck zum Kurs
        </Button>
      </Box>
    );
  }

  const isEnrolled = course.enrollment?.status === 'enrolled' || course.enrollment?.status === 'in_progress' || course.enrollment?.status === 'completed';

  // Calculate total duration
  const totalDuration = module.lessons.reduce(
    (sum, l) => sum + (l.estimatedDurationMinutes || 0),
    0
  );

  // Find first lesson
  const firstLesson = module.lessons.sort((a, b) => a.sortOrder - b.sortOrder)[0];

  const handlePlayLesson = (lesson: Lesson) => {
    if (!isEnrolled) {
      enrollMutation.mutate(undefined, {
        onSuccess: () => {
          navigate(`/courses/${course.id}/lessons/${lesson.id}`);
        },
      });
    } else {
      navigate(`/courses/${course.id}/lessons/${lesson.id}`);
    }
  };

  const handleStartModule = () => {
    if (firstLesson) {
      handlePlayLesson(firstLesson);
    }
  };

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate('/courses')}
        >
          {t('courses.title')}
        </Link>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate(`/courses/${course.id}`)}
        >
          {course.title}
        </Link>
        <Typography color="text.primary">{module.title}</Typography>
      </Breadcrumbs>

      {/* Module Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <SchoolIcon color="primary" />
              <Typography variant="h4">
                {module.title}
              </Typography>
            </Box>
            {module.description && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {module.description}
              </Typography>
            )}

            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                icon={<LessonsIcon />}
                label={`${module.lessons.length} Lektionen`}
                variant="outlined"
                size="small"
              />
              {totalDuration > 0 && (
                <Chip
                  icon={<TimerIcon />}
                  label={`${totalDuration} Min.`}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                textAlign: 'center',
                bgcolor: 'background.default',
              }}
            >
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<StartIcon />}
                onClick={handleStartModule}
                disabled={enrollMutation.isPending || !firstLesson}
              >
                {enrollMutation.isPending ? t('common.loading') : 'Modul starten'}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Lessons List */}
      <Typography variant="h5" gutterBottom>
        Lektionen ({module.lessons.length})
      </Typography>

      <Card>
        <List>
          {module.lessons
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((lesson, index) => (
              <React.Fragment key={lesson.id}>
                {index > 0 && <Divider />}
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handlePlayLesson(lesson)}
                  >
                    <ListItemIcon>
                      <PlayIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={lesson.title}
                      secondary={
                        lesson.estimatedDurationMinutes
                          ? `${lesson.estimatedDurationMinutes} Min.`
                          : undefined
                      }
                    />
                    <Chip
                      size="small"
                      label={`${lesson.pointsReward} P`}
                      variant="outlined"
                    />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))}
        </List>
      </Card>

      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate(`/courses/${course.id}`)}
        sx={{ mt: 3 }}
      >
        Zurueck zum Kurs
      </Button>
    </Box>
  );
}

export default ModuleDetailPage;
